import React, { useState, useContext, useEffect } from "react";
import { Tabs, message } from "antd";
import { Sidebar, ContainerDiv } from "../../components";
import OrgTable from "./org/OrgTable";
import SiteTable from "./site/SiteTable";
import CropTable from "./crop/CropTable";
import PlotTable from "./plot/PlotTable";
import UserTable from "./user/UserTable";
import GreenhouseTable from "./greenhouse/GreenhouseTable";
import RolesPermissionsTab from "./roles_permissions/RolesPermissionsTab";
import { useLocalStorage } from "../../hooks";
import { UserContext } from "../../context/UserContext";
import { getOrgs, getSites, getCrops, getOrgData } from "../../utils/api";
import VarietyTable from "./variety/VarietyTable";
import PlotDataVisibilityTable from "./plot_data_visibility/PlotDataVisibilityTable.jsx";

const Admin = () => {
  const { userData } = useContext(UserContext);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [prevActiveTab, setPrevActiveTab] = useLocalStorage(
    "adminActiveTab",
    "org",
  );
  const [activeTab, setActiveTab] = useState(prevActiveTab || "org");
  const [currentOrg, setCurrentOrg] = useState(null);
  const [orgData, setOrgData] = useState([]);
  const [currentSite, setCurrentSite] = useState(null);
  const [siteData, setSiteData] = useState([]);
  const [currentCrop, setCurrentCrop] = useState(null);
  const [cropData, setCropData] = useState([]);
  const [allCrops, setAllCrops] = useState([]);

  const [sideBarType, setSideBarType] = useState(null);

  const getSidebarType = () => {
    if (!currentCrop) {
      setSideBarType("default");
    }
    const currentOrgData = orgData.find((org) => org.id === currentOrg);
    const currentCropData = cropData.find((crop) => crop.id === currentCrop);
    if (
      currentOrgData?.is_grower &&
      (currentCropData?.name.toLowerCase() === "tomato" ||
        currentCropData?.name.toLowerCase() === "strawberry" ||
        currentCropData?.name.toLowerCase() === "blueberry")
    ) {
      setSideBarType("TomatoGrower");
    } else {
      setSideBarType("default");
    }
  };

  const fetchOrgs = async () => {
    try {
      const res = await getOrgs();
      const newData = res.data?.data
        ?.map((org) => {
          return { name: org.name, key: org.id };
        })
        .sort((a, b) =>
          (a?.name || "").localeCompare(b?.name || "", undefined, {
            sensitivity: "base",
          }),
        );
      setOrgData(newData);
      setCurrentOrg(newData[0].key);
    } catch (err) {
      message.error("Error fetching Organizations!");
    }
  };

  const fetchSites = async (orgId) => {
    try {
      const res = await getSites(orgId);
      const data = res.data?.data
        ?.map((site) => {
          return { ...site, key: site.id };
        })
        .sort((a, b) =>
          (a?.display_name || a?.name || "").localeCompare(
            b?.display_name || b?.name || "",
            undefined,
            { sensitivity: "base" },
          ),
        );
      setSiteData(data);
      setCurrentSite(data[0]?.key || null);
    } catch (err) {
      message.error("Error fetching Sites!");
    }
  };

  const fetchCrops = async (siteId) => {
    try {
      const res = await getCrops(siteId);
      const data = res.data.data
        .filter((crop) => crop.is_active)
        .map((crop) => {
          return { ...crop, key: crop.id };
        });
      setCropData(data);
      setCurrentCrop(data[0]?.key || null);
    } catch (err) {
      message.error("Error fetching Crops!");
    }
  };

  const fetchAllCrops = async () => {
    try {
      let data;
      if (userData?.is_superuser) {
        const res = await getCrops();
        data = res.data.data
          .filter((crop) => crop.is_active)
          .map((crop) => {
            return { ...crop, key: crop.id };
          });
      } else {
        const res = await getOrgData(userData?.organization_id);
        data = res.data.data.crops.map((crop) => {
          return { ...crop, key: crop.id };
        });
      }
      setAllCrops(data);
    } catch (err) {
      message.error("Error fetching all Crops!");
    }
  };

  useEffect(() => {
    if (userData) {
      if (userData.is_superuser) {
        fetchOrgs();
      } else {
        setOrgData([userData?.organization]);
        setCurrentOrg(userData?.organization_id);
        if (userData.is_admin) {
          const uniqueMap = new Map();

          userData?.sites.forEach((site) => {
            if (!uniqueMap.has(site.id)) {
              uniqueMap.set(site.id, { ...site, key: site.id });
            }
          });

          userData?.site_admins.forEach((site) => {
            if (!uniqueMap.has(site.id)) {
              uniqueMap.set(site.id, { ...site, key: site.id });
            }
          });

          const uniqueSites = Array.from(uniqueMap.values()).sort((a, b) =>
            (a?.display_name || a?.name || "").localeCompare(
              b?.display_name || b?.name || "",
              undefined,
              { sensitivity: "base" },
            ),
          );
          setSiteData(uniqueSites);
          setCurrentSite(
            uniqueSites && uniqueSites.length > 0
              ? uniqueSites[0]?.key || null
              : null,
          );
        } else {
          const siteData = userData?.site_admins
            .map((site) => ({
              ...site,
              key: site.id,
            }))
            .sort((a, b) =>
              (a?.display_name || a?.name || "").localeCompare(
                b?.display_name || b?.name || "",
                undefined,
                { sensitivity: "base" },
              ),
            );
          setSiteData(siteData);
          setCurrentSite(
            siteData && siteData.length > 0 ? siteData[0]?.key || null : null,
          );
        }
      }
    }
  }, [userData]);

  useEffect(() => {
    if (currentOrg && (userData?.is_superuser || userData?.is_admin)) {
      fetchSites(currentOrg);
    }
  }, [currentOrg]);

  useEffect(() => {
    if (currentSite) {
      fetchCrops(currentSite);
    }
  }, [currentSite]);

  useEffect(() => {
    if (currentCrop) {
      getSidebarType();
    }
  }, [currentCrop]);

  useEffect(() => {
    fetchAllCrops();
  }, []);

  const onChange = (key) => {
    setActiveTab(key);
    setPrevActiveTab(key);
  };

  const items = [
    {
      key: "org",
      label: `Organization`,
      children: <OrgTable cropData={allCrops} />,
      visible: userData?.is_superuser,
    },
    {
      key: "roles_permissions",
      label: `Roles & Permissions`,
      children: <RolesPermissionsTab />,
      visible: userData?.is_superuser,
    },
    {
      key: "site",
      label: `Site`,
      children: (
        <SiteTable
          currentOrg={currentOrg}
          setCurrentOrg={setCurrentOrg}
          orgData={orgData}
          cropData={allCrops}
        />
      ),
      visible: true,
    },
    {
      key: "plot_data_visibility",
      label: `Data Release`,
      children: (
        <PlotDataVisibilityTable
          plotLabel={sideBarType === "TomatoGrower" ? "Bay" : `Row`}
          greenhouseLabel={
            sideBarType === "TomatoGrower" ? "Glasshouse" : `Bay`
          }
          currentOrg={currentOrg}
          setCurrentOrg={setCurrentOrg}
          orgData={orgData}
          currentSite={currentSite}
          setCurrentSite={setCurrentSite}
          siteData={siteData}
          currentCrop={currentCrop}
          setCurrentCrop={setCurrentCrop}
          cropData={cropData}
        />
      ),
      visible: userData?.is_superuser,
    },
    {
      key: "variety",
      label: "Variety",
      children: (
        <VarietyTable
          varietyLabel={"Variety"}
          currentOrg={currentOrg}
          setCurrentOrg={setCurrentOrg}
          orgData={orgData}
          currentSite={currentSite}
          setCurrentSite={setCurrentSite}
          siteData={siteData}
          currentCrop={currentCrop}
          setCurrentCrop={setCurrentCrop}
          cropData={cropData}
        />
      ),
      visible: true,
    },
    {
      key: "plot",
      label: sideBarType === "TomatoGrower" ? "Bay" : `Plot`,
      children: (
        <PlotTable
          plotLabel={sideBarType === "TomatoGrower" ? "Bay" : `Plot`}
          currentOrg={currentOrg}
          setCurrentOrg={setCurrentOrg}
          orgData={orgData}
          currentSite={currentSite}
          setCurrentSite={setCurrentSite}
          siteData={siteData}
          currentCrop={currentCrop}
          setCurrentCrop={setCurrentCrop}
          cropData={cropData}
        />
      ),
      visible: true,
    },
    {
      key: "greenhouse",
      label: sideBarType === "TomatoGrower" ? "Glasshouse" : `Greenhouse`,
      children: (
        <GreenhouseTable
          greenhouseLabel={
            sideBarType === "TomatoGrower" ? "Glasshouse" : `Greenhouse`
          }
          currentOrg={currentOrg}
          setCurrentOrg={setCurrentOrg}
          orgData={orgData}
          currentSite={currentSite}
          setCurrentSite={setCurrentSite}
          siteData={siteData}
        />
      ),
      visible: true,
    },
    {
      key: "user",
      label: `User`,
      children: (
        <UserTable
          currentOrg={currentOrg}
          setCurrentOrg={setCurrentOrg}
          orgData={orgData}
          currentSite={currentSite}
          setCurrentSite={setCurrentSite}
          siteData={siteData}
        />
      ),
      visible: true,
    },
    {
      key: "crop",
      label: `Crop`,
      children: <CropTable />,
      visible: userData?.is_superuser,
    },
  ];

  return (
    <ContainerDiv>
      <div id="admin">
        <Sidebar
          sidebarType={sideBarType}
          active="admin"
          open={sidebarOpen}
          setOpen={setSidebarOpen}
        />
        <div
          className={`flex w-full px-20 ${sidebarOpen ? "pl-60" : ""} py-10 sm:py-4 sm:pr-6 justify-center transition-padding ease-in-out duration-200`}
        >
          <div className="w-full">
            <Tabs
              defaultActiveKey={activeTab}
              items={items.filter((item) => item.visible)}
              onChange={onChange}
            />
          </div>
        </div>
      </div>
    </ContainerDiv>
  );
};

export default Admin;
